import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ActionsButton from 'src/components/actions-button/ActionsButton';
import { useSmartCopyAmplitude } from 'src/hooks/smart-copy';
import { getSmartCopyTemplateActionState } from 'src/utils/smart-copy';

import { addCustomDatasourceUrl } from '@/utils/urls';

import type { CopyCustomDatasourceNavState } from './customDatasourceSmartCopy';
import { CUSTOM_DATASOURCE_SMART_COPY } from './customDatasourceSmartCopy';

type CustomDatasourceRegisterActionsProps = {
  /**
   * Whether the Custom Data Source Smart Copy flag is on. Read at the call site
   * (`Page.tsx`) via the OpenFeature + Meta.features hybrid, so the same value
   * also gates the register table's row selection.
   */
  isSmartCopyEnabled: boolean;
  /** Rows currently selected in the register table. "Copy" enables at exactly 1. */
  selectedCount: number;
  /**
   * Id of the selected source row, set only when exactly one is selected. Carried
   * to the create page via nav state so it opens pre-populated from that source.
   */
  selectedId?: string;
  /**
   * Resolved by the caller rather than here, so this component needs no
   * permissions provider. The register's checks are type-level, so a boolean is
   * equivalent to a `<Permission>` gate.
   */
  canInsert: boolean;
  canDelete: boolean;
  isDeleteEnabled: boolean;
  onDelete: () => void;
};

/**
 * Register-header action controls for Custom Data Sources.
 *
 * Delete renders first so it sits to the left of the create control, matching
 * the Indicator and Control-Test registers. It is gated on `delete` while
 * Add/Copy are gated on `insert`, so a user holding only one of the two still
 * sees the half they can use.
 */
export const CustomDatasourceRegisterActions: FC<
  CustomDatasourceRegisterActionsProps
> = ({
  isSmartCopyEnabled,
  selectedCount,
  selectedId,
  canInsert,
  canDelete,
  isDeleteEnabled,
  onDelete,
}) => {
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'customDatasources',
  });
  const navigate = useNavigate();
  // No event can fire unless the dropdown renders, which needs both Smart Copy
  // and insert access.
  const { events, logEvent } = useSmartCopyAmplitude({
    entityLabel: CUSTOM_DATASOURCE_SMART_COPY.eventLabel,
  });

  const templateActionState = getSmartCopyTemplateActionState(t, {
    entityNoun: CUSTOM_DATASOURCE_SMART_COPY.entityNoun,
    selectedCount,
  });

  // Render nothing rather than an empty wrapper, which would still take a gap
  // from the surrounding horizontal `SpaceBetween`.
  if (!canInsert && !canDelete) {
    return null;
  }

  return (
    <SpaceBetween direction={'horizontal'} size={'xs'}>
      {canDelete && (
        <Button
          formAction={'none'}
          variant={'normal'}
          disabled={!isDeleteEnabled}
          onClick={onDelete}
        >
          {st('delete_button')}
        </Button>
      )}
      {canInsert &&
        (isSmartCopyEnabled ? (
          <ActionsButton
            buttonText={st('actions_button')}
            testId={'customDatasourceActions'}
            variant={'primary'}
            onOpen={() => logEvent(events.DROPDOWN_OPENED, {})}
            items={[
              {
                id: 'addCustomDatasource',
                text: st('create_new_button'),
                onItemClick: () => navigate(addCustomDatasourceUrl()),
              },
              {
                id: 'copyCustomDatasource',
                text: st('add_custom_datasource_from_template'),
                ...templateActionState,
                onItemClick: () => {
                  if (!selectedId) {
                    return;
                  }
                  logEvent(events.TEMPLATE_SELECTED, {
                    sourceCustomDatasourceId: selectedId,
                  });
                  const state: CopyCustomDatasourceNavState = {
                    copyFromCustomDatasourceId: selectedId,
                  };
                  navigate(addCustomDatasourceUrl(), { state });
                },
              },
            ]}
          />
        ) : (
          <Button variant={'primary'} href={addCustomDatasourceUrl()}>
            {st('create_new_button')}
          </Button>
        ))}
    </SpaceBetween>
  );
};
